import { ApiService } from '@api/services/api/api.service';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { DeviceService } from '../device/device.service';
import { InfrastructureChecksModel } from './models';
import { compare } from 'compare-versions';
import { AppService } from '../app/app.service';
import {
  InfrastructureDataDTO,
  InfrastructurePlatformDTO
} from '@core/modules/api/models';

@Injectable({
  providedIn: 'root'
})
export class InfrastructureService {
  constructor(
    private deviceService: DeviceService,
    private appService: AppService,
    private apiService: ApiService
  ) {}

  /**
   * restituisce l'esito dei controlli sull'infrastruttura
   */
  getInfrastructureChecks(): Promise<InfrastructureChecksModel> {
    if (!this.isInfrastructureCheckRequired()) {
      return this.getStandardChecks();
    } else {
      return lastValueFrom(this.apiService.getMobileStatus({})).then(data =>
        this.getComparedChecks(data)
      );
    }
  }

  /**
   * esegui i controlli senza i dati determinati dall'infrastruttura
   */
  private async getStandardChecks(): Promise<InfrastructureChecksModel> {
    const appInfo = await this.appService.getAppInfo();

    return {
      systemAvailable: true,
      versionAllowed: true,
      currentVersion: appInfo.version,
      requiredVersion: appInfo.version
    };
  }

  /**
   * esegui i controlli confrontando i dati dell'infrastruttura con quelli del device
   */
  private async getComparedChecks(
    data: InfrastructureDataDTO
  ): Promise<InfrastructureChecksModel> {
    const appInfo = await this.appService.getAppInfo();
    const platform = await this.selectPlatformData(data);
    const currentVersion = appInfo.version;
    const requiredVersion = platform && platform.minAppVersion;

    return {
      systemAvailable: data.systemAvailable,
      versionAllowed: this.isAllowedVersion(currentVersion, requiredVersion),
      currentVersion,
      requiredVersion,
      updateLink: platform && platform.appStoreLink
    };
  }

  /**
   * seleziona i dati della piattaforma appropriata in base al dispositivo
   */
  private async selectPlatformData(
    data: InfrastructureDataDTO
  ): Promise<InfrastructurePlatformDTO | undefined> {
    const deviceInfo = await this.deviceService.getDeviceInfo();

    if (deviceInfo.platform === 'ios') {
      return data.ios;
    } else if (deviceInfo.platform === 'android') {
      return data.android;
    }
  }

  /**
   * controlla se la versione corrente è valida
   */
  private isAllowedVersion(current: string, required: string): boolean {
    if (!current || !required) {
      return true;
    } else {
      return compare(current, required, '>=');
    }
  }

  /**
   * valuta se il controllo dell'infrastruttura è richiesto
   */
  private isInfrastructureCheckRequired(): boolean {
    return !!this.appService.getConfig().security.checkInfrastructure === true;
  }
}
